

import { useTranslation } from 'react-i18next';
import './BookingDataForm.scss'
import { Button, Checkbox, Col, Divider, Form, Input, Modal, notification, Popover, Radio, Row, Space, Spin, Typography } from 'antd';
import { useCart } from '../../../Contexts/CartContexts';
import { CartRoomData } from '@beds2b-group/reusable-components/dist/types';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import PerformBooking, { Pax, Policy } from '../../../models/booking/PerformBooking';
import { useSessionData } from '../../../Contexts/SessionDataContext';
import { changeBookingStatus, createPendingBooking, deletePendingBooking, doPreAuthorizationPayment, fetchBookingData, fetchBookingStatus, GetPaymentMethodById, performBooking, voidTPVPayment } from '../../../api/repositoryEP';
import { useNavigate } from "react-router-dom";
import { PaymentMethodData } from '../../../models/PaymentsMethodsData';
import { PreAuthorizationTPVData } from '../../../models/entities/booking/TPVData';
import { GetFormattedUrl } from '../../../utils/urls';
import LoadingDataComponent from '../../common/LoadingData/LoadingDataComponent';
import { BookingStatusInfo, CANCELLED_BY_USER, ErrorStatus, GUEST, MINIMUM, AGENT, TOTAL } from '../../../utils/constants';
import { useCurrencyData } from '../../../Contexts/CurrencyContext';
import MoneyComponent from '../../common/MoneyComponent/MoneyComponent';
import dayjs from "dayjs";
import { GenericGlobalKeyValuePair } from '../../../models/CustomField';
import CardTokenizeForm from '../../PCIProxy/PCIProxy';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from "libphonenumber-js";
import { useAppConfig } from '../../../Contexts/AppConfigContext';
import { use } from 'echarts';
import i18n from '../../../i18n/i18n';
import moment from 'moment';
import NMIComponent from '../../NMI/NmiComponent';





interface BookingDataFormProps {
    availData: any,
    paymentsMethods: PaymentMethodData[],
    originalCurrency: string,
    countries: GenericGlobalKeyValuePair<string, string>[],
    doExchangeCurrency: (fromCurrency: string, toCurrency: string, quantity: number) => number,
    onFormValidityChange: (isValid: boolean) => void;
    setBookingId: (id: string) => void;

}

const BookingDataForm = forwardRef<any, BookingDataFormProps>(function BookingDataForm(props, ref) {



    const userSessionData = useSessionData();
    const [formPaymentData, setFormPaymentData] = useState<string>("");
    const [form] = Form.useForm();
    const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
    const [bookingId, setBookingId] = useState<string>("");
    const [isConfirmBooking, setIsConfirmBooking] = useState<boolean>(false);
    const [preAuthorizationFailed, setPreAuthorizationFailed] = useState<boolean>(false);
    const [lastPaymentStatusInfo, setLastPaymentStatusInfo] = useState<string | null>(null);
    const [loadingCancelation, setLoadingCancelation] = useState<boolean>(false);
    const [loadingRetry, setLoadingRetry] = useState<boolean>(false);
    const [showNMI, setShowNMI] = useState<boolean>(false);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const appConfig = useAppConfig();


    const cartContext = useCart();
    const sessionDataContext = useSessionData();
    const payButtonRef = useRef<HTMLButtonElement | null>(null); // Referencia del botón de pago
    const popupRef = useRef<Window | null>(null);
    const handledPopupClose = useRef(false);


    // Exponemos la función `scrollToButton` para que otro componente pueda usarla
    useImperativeHandle(ref, () => ({
        scrollToButton: () => {
            if (payButtonRef.current) {
                payButtonRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        },
        createPostponedBooking: async () => {
            const bookingId = await createBookingInternal(true);
            return bookingId;
        }
    }));

    const createBookingInternal = async (postponed: boolean): Promise<string | null> => {
        try {
            const performBookingData = buildPerformBookingData(); // ← tu código actual

            const commissionConfigurationIsBase = appConfig.config.userCommissionConfiguration
                ?.find(c => c.id === sessionDataContext.userData.commissionConfigurationSelected)?.isBase ?? false;
            const response = await createPendingBooking(
                performBookingData,
                sessionDataContext.userData.commissionConfigurationSelected || null,
                commissionConfigurationIsBase
            );

            if (response?.type === 1) {
                const id = response.data;

                props.setBookingId(id);

                if (postponed) {
                    await changeBookingStatus(id, "postponed");
                }

                return id;
            }

            return null;
        } catch (e) {
            console.error(e);
            return null;
        }
    };


    const { doExchangeCurrency, currencyData } = useCurrencyData();

    const [isWaitingPayment, setIsWaitingPayment] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(360); // 6 minutos en segundos

    const [cardPaymentOption, setCardPaymentOption] = useState<typeof GUEST | typeof AGENT>("guest");
    const [cardPaymentQuantity, setCardPaymentQuantity] = useState<typeof MINIMUM | typeof TOTAL>(TOTAL);
    const onChangeSelectedPaymentType = (e: any) => {
        setSelectedPaymentType(e.target.value);
        // Las opciones de tarjeta solo aplican al TPV: al cambiar de método volvemos a los valores
        // por defecto para no arrastrar "agente" o "mínimo" de un método anterior al totalToPay o al cobro
        setCardPaymentOption(GUEST);
        setCardPaymentQuantity(TOTAL);
    }
    const onChangeCardPaymentOption = (e: any) => {
        setCardPaymentOption(e.target.value);
    }
    const onChangeCardPaymentQuantity = (e: any) => {
        setCardPaymentQuantity(e.target.value);
    }


    const calculateMinimumQuantityToPay = (): number => {
        const rooms = cartContext.data?.rooms ?? [];
        let total = 0;

        rooms.forEach((room) => {
            const depositInfo = room.rateInfo?.depositInfo;
            if (!depositInfo) return;

            const { depositType, amount, beforeDate } = depositInfo;

            //  Comprobamos si el beforeDate es HOY (Dayjs)
            const isToday = beforeDate && dayjs(beforeDate).isSame(dayjs(), "day");
            if (!isToday) return; //  si no es hoy, lo saltamos

            //  Precio base según tipo de tarjeta y tipo de precio (PVP o NET)
            const rawBasePrice = cardPaymentOption === "agent"
                ? (room.price.net ?? room.price.pvp ?? 0)
                : room.price.isPvp
                    ? (room.price.pvp ?? 0)
                    : (room.price.net ?? 0) + (room.price.commission ?? 0);

            // Convertir a la moneda de display
            const basePrice = doExchangeCurrency(room.price.currency, currencyData.currencySelectedISO, rawBasePrice);

            //  Calcular el importe mínimo según tipo de depósito
            if (depositType === "Fixed") {
                total += doExchangeCurrency(room.price.currency, currencyData.currencySelectedISO, amount);
            } else { // POR PORCENTAJE
                total += basePrice * (amount / 100);
            }
        });

        return total;
    };

    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);



    const calculateTotalQuantityToPay = (): number => {
        const rooms = cartContext.data?.rooms ?? [];
        const services = cartContext.data?.services ?? [];

        const totalRooms = rooms.reduce((total, room) => {
            const rawAmount = cardPaymentOption === "agent"
                ? (room.price.net ?? room.price.pvp ?? 0)
                : room.price.isPvp
                    ? (room.price.pvp ?? 0)
                    : (room.price.net ?? 0) + (room.price.commission ?? 0);
            const amount = doExchangeCurrency(room.price.currency, currencyData.currencySelectedISO, rawAmount);
            return total + amount;
        }, 0);

        const totalServices = services.reduce((total, service) => {
            const rawAmount = cardPaymentOption === "agent"
                ? (service.price.net ?? service.price.pvp ?? 0)
                : service.price.isPvp
                    ? (service.price.pvp ?? 0)
                    : (service.price.net ?? 0) + (service.price.commission ?? 0);
            const amount = doExchangeCurrency(service.price.currency, currencyData.currencySelectedISO, rawAmount);
            return total + amount;
        }, 0);

        return totalRooms + totalServices;
    };








    const [minimumAmount, setMinimumAmount] = useState<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);

    useEffect(() => {
        const min = calculateMinimumQuantityToPay();
        const total = calculateTotalQuantityToPay();
        setMinimumAmount(min);
        setTotalAmount(total);

    }, [cartContext.data, cardPaymentOption, currencyData.currencySelectedISO]); // recalcular solo cuando cambie el carrito o la opcion de pago


    // aqui habra qeu tener en cuenta si es pago minimo o total , es deci
    const getPaymentForm = async (bngId: string, paymentToken: string | null = null): Promise<boolean> => {

        // El TPV siempre cobra en la moneda ORIGINAL de disponibilidad (props.originalCurrency),
        // independientemente de la divisa de display seleccionada por el usuario.
        // Recalculamos el importe directamente desde la moneda del carrito (room.price.currency)
        // para evitar dobles conversiones.
        const rooms = cartContext.data?.rooms ?? [];
        const services = cartContext.data?.services ?? [];

        let rawAmount: number;
        if (cardPaymentQuantity === "minimum") {
            // Recalcular mínimo en originalCurrency
            const rooms = cartContext.data?.rooms ?? [];
            rawAmount = rooms.reduce((total, room) => {
                const depositInfo = room.rateInfo?.depositInfo;
                if (!depositInfo) return total;
                const { depositType, amount, beforeDate } = depositInfo;
                const isToday = beforeDate && dayjs(beforeDate).isSame(dayjs(), "day");
                if (!isToday) return total;
                const rawBase = cardPaymentOption === "agent"
                    ? (room.price.net ?? room.price.pvp ?? 0)
                    : room.price.isPvp ? (room.price.pvp ?? 0) : (room.price.net ?? 0) + (room.price.commission ?? 0);
                const baseInOrig = props.doExchangeCurrency(room.price.currency, props.originalCurrency, rawBase);
                if (depositType === "Fixed") {
                    return total + props.doExchangeCurrency(room.price.currency, props.originalCurrency, amount);
                } else {
                    return total + baseInOrig * (amount / 100);
                }
            }, 0);
        } else {
            // Recalcular en moneda original (originalCurrency) sumando room por room
            rawAmount = rooms.reduce((total, room) => {
                const base = cardPaymentOption === "agent"
                    ? (room.price.net ?? room.price.pvp ?? 0)
                    : room.price.isPvp
                        ? (room.price.pvp ?? 0)
                        : (room.price.net ?? 0) + (room.price.commission ?? 0);
                return total + props.doExchangeCurrency(room.price.currency, props.originalCurrency, base);
            }, 0) + services.reduce((total, service) => {
                const base = cardPaymentOption === "agent"
                    ? (service.price.net ?? service.price.pvp ?? 0)
                    : service.price.isPvp
                        ? (service.price.pvp ?? 0)
                        : (service.price.net ?? 0) + (service.price.commission ?? 0);
                return total + props.doExchangeCurrency(service.price.currency, props.originalCurrency, base);
            }, 0);
        }

        let currency = props.originalCurrency;
        let amount = Number(rawAmount.toFixed(2));
        const data: PreAuthorizationTPVData = {
            idUser: userSessionData.userData.id || "",
            bookingId: bngId || bookingId,
            amount: amount,
            currency: currency,
            street1: form.getFieldValue('street1') || "",
            street2: form.getFieldValue('street2') || "",
            street3: form.getFieldValue('street3') || null,
            city: form.getFieldValue('city') || "",
            postalCode: form.getFieldValue('postalCode') || "",
            country: form.getFieldValue('country') || "",
            paymentToken: paymentToken
        };
        const response = await doPreAuthorizationPayment(data);
        if (response?.type == 1 && data) {

            setFormPaymentData(response.data.form);

            if (isNMISelected) {
                // NMI: no abrimos ventana, solo esperamos cambio de estado
                setIsWaitingPayment(true);
                return true;
            }

            if (isStripeSelected) {

                const urlForm = response.data.form;
                window.open(urlForm, '_blank', 'noopener,noreferrer');


                setIsWaitingPayment(true);
                return true;
            }

            const popup = window.open('', 'tpvWindow', 'width=600,height=800');
            popupRef.current = popup;

            if (popup && response.data.form) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(response.data.form, "text/html");
                const form = doc.querySelector("form");

                if (form) {
                    form.setAttribute("target", "tpvWindow");
                    document.body.appendChild(form); // lo añades al DOM actual
                    form.submit();
                    form.remove(); // limpieza
                    setIsWaitingPayment(true);
                } else {
                    console.error("No se encontró el form en response.data.form");
                    return false;
                }
            }
            return true;
        }
        return false;
    };


    useEffect(() => {
        if (formPaymentData) {
            // Esperamos un pequeño delay para asegurar que el DOM del modal está montado
            setTimeout(() => {
                const container = document.getElementById("tpv-form-container");
                if (container) {
                    const form = container.querySelector("form");
                    if (form) {
                        form.setAttribute("target", "tpvIframe");
                        form.submit();
                        setIsWaitingPayment(true);
                    }
                }
            }, 100);
        }
    }, [formPaymentData]);



    const validatePhone = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(t("forms.validations.phone-no-valid")!);
        }

        // Use ownerPhone (dialCode|nationalNumber) which is normalized in onChange
        const ownerPhoneVal: string = form.getFieldValue("ownerPhone") || "";
        const parts = ownerPhoneVal.split("|");
        const normalizedPhone =
            parts.length === 2 && parts[0] && parts[1]
                ? `+${parts[0]}${parts[1].replace(/^0+/, "")}`
                : `+${String(value).replace(/\D/g, "")}`;

        try {
            if (normalizedPhone.length > 4 && isValidPhoneNumber(normalizedPhone)) {
                return Promise.resolve();
            }
        } catch {}

        return Promise.reject(t("forms.validations.phone-no-valid")!);
    };
    const language = i18n.language;


    const buildPerformBookingData = (): PerformBooking => {
        var cartData = cartContext.data;


        const hotel = appConfig.config.hotels.find(h => h.integrationCode === cartContext.data.hotelData.code);

        const paxes: Pax[] = [];
        const roomKeys = Object.keys(form.getFieldsValue()).filter((key) => key.includes('.name'));

        roomKeys.forEach((key) => {
            var roomKeyData = key.split('.');
            const roomIndex = parseInt(roomKeyData[1]);
            const paxType = roomKeyData[2];
            const paxIndex = parseInt(roomKeyData[3]);

            const name = form.getFieldValue(`room.${roomIndex}.${paxType}.${paxIndex}.name`);
            const firstSurname = form.getFieldValue(`room.${roomIndex}.${paxType}.${paxIndex}.firstSurname`);
            const secondSurname = form.getFieldValue(`room.${roomIndex}.${paxType}.${paxIndex}.secondSurname`);
            const age = form.getFieldValue(`room.${roomIndex}.${paxType}.${paxIndex}.age`);

            paxes.push({
                paxType: paxType == 'adult' ? 0 : 2,
                roomIndex: roomIndex,
                roomCode: cartData.rooms[roomIndex].code,
                name: name,
                firstSurname: firstSurname,
                secondSurname: secondSurname,
                age: age
            });
        });

        const isNetPrice = cartData.rooms.some((room) => !room.price.isPvp);
        const refCodePayment = props.paymentsMethods.find(pm => pm.id === selectedPaymentType)?.refCode || "";
        const performBookingData: PerformBooking = {
            billingAddressDto: {
                street1: form.getFieldValue('street1') || "",
                street2: form.getFieldValue('street2') || "",
                street3: form.getFieldValue('street3') || "",
                city: form.getFieldValue('city') || "",
                country: form.getFieldValue('country') || "",
                postalCode: form.getFieldValue('postalCode') || "",
            },
            availabilityRequestId: cartData.cartHash,
            holder: {
                name: form.getFieldValue('ownerName'),
                firstSurname: form.getFieldValue('firstSurname'),
                secondSurname: form.getFieldValue('secondSurname') || "",
                email: form.getFieldValue('ownerEmail'),
                phone: {
                    prefix: '',
                    number: form.getFieldValue('ownerPhone')
                }
            },
            marketCode: sessionStorage.getItem('integration-simulation-market') || sessionDataContext.userData.market,
            paymentMethod: selectedPaymentType,
            isNetPrice: isNetPrice,
            observationsForHotel: form.getFieldValue('observations'),
            paxes: paxes,
            hotelData: {
                address: cartData.hotelData.address,
                code: cartData.hotelData.code,
                name: hotel?.name || cartData.hotelData.name,
                email: cartData.hotelData.email,
                mainImage: cartData.hotelData.mainImage ?? '',
                personOfContact: cartData.hotelData.personOfContact,
                phone: cartData.hotelData.phone,
                website: cartData.hotelData.website,
                longitude: 0,
                latitude: 0,
            },
                accommodationDates: {
                from: dayjs(cartData.dates.checkIn).format("YYYY-MM-DD"),
                to: dayjs(cartData.dates.checkOut).format("YYYY-MM-DD")
            },
            roomInfo: cartData.rooms.map((room) => ({
                roomIndex: room.roomIndex,
                mainImage: room.mainImage,
                code: room.code,
                name: appConfig.config.rooms
                    .find(r => r.integrationCode === room.code)
                    ?.names.find(n => n.language.startsWith(language))
                    ?.value
                    || room.name,
                // El carrito guarda los precios en la moneda de display; a la API se mandan
                // en la moneda original de la disponibilidad, que es en la que se cobra y se guarda
                priceInfo: {
                    currency: props.originalCurrency,
                    pvp: props.doExchangeCurrency(room.price.currency, props.originalCurrency, room.price.pvp),
                    net: props.doExchangeCurrency(room.price.currency, props.originalCurrency, room.price.net),
                    commission: props.doExchangeCurrency(room.price.currency, props.originalCurrency, room.price.commission),
                    isPvp: room.price.isPvp
                },
                occupancy: {
                    adults: room.occupancy.adults,
                    children: room.occupancy.childs,
                    childrenAges: room.occupancy.childsAges
                },
                mealInfo: room.mealInfo,
                rateInfo: {
                    ...room.rateInfo,
                    cancelPolicies: {
                        ...room.rateInfo.cancelPolicies,
                        policies: (room.rateInfo.cancelPolicies.policies as Policy[]).filter(
                            (policy) => policy.code === room.mealInfo.code
                        )
                    }
                }
            })),
            serviceInfo: cartData.services.map((service) => ({
                roomIndex: service.serviceIndex,
                mainImage: service.mainImage,
                code: service.code,
                name: service.name,
                priceInfo: {
                    currency: props.originalCurrency,
                    pvp: props.doExchangeCurrency(service.price.currency, props.originalCurrency, service.price.pvp),
                    net: props.doExchangeCurrency(service.price.currency, props.originalCurrency, service.price.net),
                    commission: props.doExchangeCurrency(service.price.currency, props.originalCurrency, service.price.commission),
                    isPvp: service.price.isPvp
                },

            })),
            bookingIdPro: "",
            typeCardPayment: (refCodePayment.toLowerCase().includes("tpv") ? cardPaymentOption : null),
            quantityCardPayment: (refCodePayment.toLowerCase().includes("tpv") ? cardPaymentQuantity : null),
            totalToPay: Number(((cartContext.data?.rooms ?? []).reduce((total, room) => {
                const base = cardPaymentOption === "agent"
                    ? (room.price.net ?? room.price.pvp ?? 0)
                    : room.price.isPvp ? (room.price.pvp ?? 0) : (room.price.net ?? 0) + (room.price.commission ?? 0);
                return total + props.doExchangeCurrency(room.price.currency, props.originalCurrency, base);
            }, 0)).toFixed(2)),
            paymentToken: tokenPCIProxy ?? null,
            expirationDateCard: expirationDatePCIProxy ?? null,

        };


        return performBookingData;

    }



    // Cambia esta función: que sea ASYNC directamente
    const confirmBooking = async () => {
        try {


            setIsConfirmBooking(true);
            setPreAuthorizationFailed(false);
            setLastPaymentStatusInfo(null);
            handledPopupClose.current = false;

            //Get cart data

            const performBookingData = buildPerformBookingData();



            // Aquí empieza la lógica principal
            const commissionConfigurationIsBase = appConfig.config.userCommissionConfiguration
                ?.find(c => c.id === sessionDataContext.userData.commissionConfigurationSelected)?.isBase ?? false;
            const response = await createPendingBooking(
                performBookingData,
                sessionDataContext.userData.commissionConfigurationSelected || null,
                commissionConfigurationIsBase
            );
            //  console.log("Response del createPendingBooking", response?.data);

            if (response && response?.type == 1) {
                setBookingId(response.data);
                props.setBookingId(response.data);
                //    console.log("create pending booking es ", response.data);

                if (isNMISelected) {
                    // NMI: mostramos el modal para tokenizar, la preautorización se hará en onSuccess
                    setShowNMI(true);
                } else if (isTPVSelected && !isPCIProxySelected) {
                    //      console.log("llamamos a getPaymentForm");
                    await getPaymentForm(response.data);
                    //        console.log("hecho el getPaymentForm");
                } else {
                    console.log("llamamos a performBooking")
                    const r = await performBooking(performBookingData, response.data);
                    if (r && r.type === 1 && r.data && r.data.locator) {
                        console.log("Booking realizado sin TPV");
                        navigate(GetFormattedUrl(`private/payment-ok-customer-voucher/${r.data.locator}`));
                    }
                }
            }
        } catch (err) {
            console.error("Error durante el booking:", err);
        } finally {
            setIsConfirmBooking(false);
        }
    };

    const handleSuccessfulBooking = async (bookingId: string) => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
            popupRef.current = null;
        }

        // Obtener los datos de la reserva para redirigir
        const bookingData = await fetchBookingData(bookingId, "", i18n.language);
        //  console.log("El booking data es:", bookingData);

        // Extraer el localizador de la reserva
        const bookingLocator = bookingData?.data?.pmsLocator || "";

        // Desactivar el estado de espera de pago
        setIsWaitingPayment(false);

        // Redirigir al voucher del cliente
        navigate(GetFormattedUrl(`private/payment-ok-customer-voucher/${bookingLocator}`));

    };

    const retryPreAuthorization = async () => {
        // console.log("Reintentando preautorización...");
        setLoadingRetry(true);
        setCountdown(360);
        //cerramos popup si está abierto
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
        }
        popupRef.current = null;


        await changeBookingStatus(bookingId, "pending", "");

        if (isNMISelected) {
            setPreAuthorizationFailed(false);
            setIsWaitingPayment(false);
            setLoadingRetry(false);
            setShowNMI(true);
        } else {
            setTimeout(async () => {
                await getPaymentForm(bookingId);

                handledPopupClose.current = false; //  Reinicia la referencia de control del cierre

                setPreAuthorizationFailed(false);
                setIsWaitingPayment(false);
                setTimeout(() => setIsWaitingPayment(true), 0); // fuerzo el tick para que cambie el estado


                setLoadingRetry(false);
            }, 200);
        }
    };


    const cancelBooking = async () => {
        setLoadingCancelation(true);

        // For settle failures we keep the booking and just leave the flow.
        if (lastPaymentStatusInfo === BookingStatusInfo.ERROR_SETTLE) {
            setPreAuthorizationFailed(false);
            setIsWaitingPayment(false);
            setLoadingCancelation(false);
            navigate(GetFormattedUrl("private/dashboard"));
            return;
        }

        //  const res = await changeBookingStatus(bookingId, ErrorStatus, CANCELLED_BY_USER);

        //    console.log("cambiado de estado ya ")
        /*notification.error({
            message: t("error.error"),
            description: t("error.cancel-payment"),
        });*/
        deletePendingBooking(bookingId)
            .then((response) => {
                if (response && response.type === 1 && response.data == false) {
                    notification.error({
                        message: t("error.error"),
                        description: t("error.cancel-booking"),
                    });
                    navigate(GetFormattedUrl("private/dashboard"));
                }
            })
            .finally(() => {
                setLoadingCancelation(false);
                setIsWaitingPayment(false);
            });
    }

    useEffect(() => {
        //   console.log("valor de isWaitingPayment es: " + isWaitingPayment) 
    }, [isWaitingPayment])



    useEffect(() => {
        let pollingInterval: NodeJS.Timeout;
        let countdownInterval: NodeJS.Timeout;
        let popupCheckInterval: NodeJS.Timeout;
        let settled = false; //  evita llamadas dobles

        //   console.log("llega al useEffect", isWaitingPayment)
        if (isWaitingPayment) {
            //    console.log("ha entrado")
            const startTime = Date.now();
            const duration = 7 * 60 * 1000; // 7 minutos
            setCountdown(duration);
            //  Polling cada 5 segundos

            pollingInterval = setInterval(async () => {
                try {
                    const res = await fetchBookingStatus(bookingId);
                    //   console.log("estado", res)

                    if (res?.type == 1) {
                        if (res.data?.status === "active" && !settled) {
                            settled = true; // marca que ya ejecutamos settle
                            console.log(res.data)
                            clearInterval(pollingInterval);
                            clearInterval(countdownInterval);
                            handleSuccessfulBooking(bookingId);


                        } else if (res.data?.statusInfo === BookingStatusInfo.ERROR_PRE_AUTHORIZATION || res.data?.statusInfo === BookingStatusInfo.CANCELLED_BY_USER) {
                            // aqui habilitamos un botón para reintentar pago y reseteamos reloj
                            setLastPaymentStatusInfo(res.data?.statusInfo || BookingStatusInfo.ERROR_PRE_AUTHORIZATION);
                            changeBookingStatus(bookingId, ErrorStatus, BookingStatusInfo.ERROR_PRE_AUTHORIZATION);
                            setPreAuthorizationFailed(true);
                            clearInterval(pollingInterval);
                            clearInterval(countdownInterval);

                        } else if (res.data?.statusInfo === BookingStatusInfo.ERROR_SETTLE) {
                            setLastPaymentStatusInfo(BookingStatusInfo.ERROR_SETTLE);
                            setPreAuthorizationFailed(true);
                            clearInterval(pollingInterval);
                            clearInterval(countdownInterval);

                        }

                    }

                } catch (error) {
                    console.error("Error consultando el estado de pago:", error);
                }
            }, 5000);

            //  Cuenta atrás
            countdownInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const remainingSeconds = Math.max(0, Math.ceil((duration - elapsed) / 1000));
                setCountdown(remainingSeconds);

                if (remainingSeconds <= 0) {
                    clearInterval(pollingInterval);
                    clearInterval(countdownInterval);

                    setIsCancelling(true); // si lo cancela , e entonces ponemos la reserva en estado error, error_preauthorization
                    handleVoidPayment().finally(() => {
                        setIsCancelling(false);
                        setIsWaitingPayment(false);
                    });
                    changeBookingStatus(bookingId, ErrorStatus, BookingStatusInfo.ERROR_PRE_AUTHORIZATION).then(() => {
                        //   console.log("cambiado el estado");
                    });
                }
            }, 1000);



            popupCheckInterval = setInterval(async () => {
                if (popupRef.current && popupRef.current.closed && !handledPopupClose.current) {
                    handledPopupClose.current = true; //  Solo entra una vez
                    clearInterval(popupCheckInterval);

                    console.log("Ventana TPV cerrada por el usuario");

                    //  Esperamos 1 segundo para dar tiempo a Redsys/GetTPVResponse
                    await new Promise(resolve => setTimeout(resolve, 1500));

                    const res = await fetchBookingStatus(bookingId);

                    if (res?.type === 1 && res?.data?.status === "pending" && res.data?.processingPayment === false) {
                        clearInterval(pollingInterval);
                        clearInterval(countdownInterval);

                        setLastPaymentStatusInfo(BookingStatusInfo.ERROR_PRE_AUTHORIZATION);
                        const r = await changeBookingStatus(bookingId, ErrorStatus, BookingStatusInfo.ERROR_PRE_AUTHORIZATION);
                       console.log("respuesta,", r)
                        setPreAuthorizationFailed(true);
                        console.log("El usuario ha cerrado la ventana de pago sin completar el proceso.");
                        notification.warning({
                            message: t("error.warning"),
                            description: t("error.payment"),
                        });
                    }
                }
            }, 1000);
        }

        // Si el usuario cierra el navegador mientras espera el pago, marcamos la reserva como error
        const handleBeforeUnload = () => {
            if (isWaitingPayment && bookingId && !settled) {
                const url = `${process.env.REACT_APP_BASE_API}/${process.env.REACT_APP_API_VERSION}/Bookings/ChangeBookingStatus`;
                const payload = JSON.stringify({
                    bookingId: bookingId,
                    status: ErrorStatus,
                    statusInfo: BookingStatusInfo.ERROR_PRE_AUTHORIZATION
                });
                const token = sessionStorage.getItem('token');
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'X-Authorization': `bearer ${token}` } : {}),
                        'Authority': window.location.host
                    },
                    body: payload,
                    keepalive: true
                });
            }
        };

        if (isWaitingPayment) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            clearInterval(pollingInterval);
            clearInterval(countdownInterval);
            clearInterval(popupCheckInterval);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isWaitingPayment, bookingId]);





    const handleVoidPayment = async () => {
        if (isCancelling) return; // previene doble ejecución

        setIsCancelling(true);
        try {

            const res = await voidTPVPayment(bookingId); // si falla el void, ya se notificado
            if (res?.type === 1 && res.data?.voidCorrect === true) {
                // SI HA IDO BIEN LA LIBERACIÓN...  flujo ok

            } else if (res?.code != 200) { // es decir que no se haya hecho bien la peticion o algo por el estilo
                await changeBookingStatus(bookingId, ErrorStatus, "ERROR_VOIDING");


            }
        } catch (error) {
            console.error("Error anulando el pago:", error);
            await changeBookingStatus(bookingId, ErrorStatus, "ERROR_VOIDING");

        } finally {
            notification.error({
                message: t("error.error"),
                description: t("error.payment"),
            });
            setIsWaitingPayment(false); // cierra el modal
            setIsCancelling(false); // vuelve a permitir cancelar si se reabre
            navigate(GetFormattedUrl("private/dashboard"));
        }
    };




    const onFinish = () => {
        // El Enter de algunos campos llama aquí directamente: no se confirma si falta algo (igual que con el botón)
        if (!canConfirmBooking) return;
        confirmBooking();
    }

    const isTPVSelected = props.paymentsMethods.some(
        (pm) => pm.id === selectedPaymentType && pm.refCode.includes("tpv")
    );

    const selectedTPVMethod = props.paymentsMethods.find(
        (method) =>
            method.id === selectedPaymentType &&
            method.refCode?.toLowerCase().includes("tpv")
    );
    const isStripeSelected = props.paymentsMethods.some(
        (pm) =>
            pm.id === selectedPaymentType &&
            pm.refCode?.toLowerCase().includes("tpv") &&
            pm.refCode?.toLowerCase().includes("stripe")
    );

    const isPCIProxySelected = props.paymentsMethods.some(
        (pm) =>
            pm.id === selectedPaymentType &&
            pm.refCode?.toLowerCase().includes("pciproxy")
    );

    const isNMISelected = props.paymentsMethods.some(
        (pm) =>
            pm.id === selectedPaymentType &&
            pm.refCode?.toLowerCase().includes("nmi")
    );

    const [merchantIdPCIProxy, setMerchantIdPCIProxy] = useState<string>("");
    const [tokenizationKeyNMI, setTokenizationKeyNMI] = useState<string>("");

    useEffect(() => {
        if (isPCIProxySelected) {
            GetPaymentMethodById(selectedPaymentType).then(response => {
                if (response?.type === 1 && response.data) {
                    const token = response.data.paymentMethodMetas.find((meta: any) => meta.key === "MerchantId")?.value || "";
                    setMerchantIdPCIProxy(token);
                }
            });
        }
    }, [isPCIProxySelected, selectedPaymentType]);

    useEffect(() => {
        if (isNMISelected) {
            GetPaymentMethodById(selectedPaymentType).then(response => {
                if (response?.type === 1 && response.data) {
                    const key = response.data.paymentMethodMetas.find((meta: any) => meta.key === "TokenizationKey")?.value || "";
                    setTokenizationKeyNMI(key);
                }
            });
        }
    }, [isNMISelected, selectedPaymentType]);



    const [tokenPCIProxy, setTokenPCIProxy] = useState<string | null>(null);
    const [expirationDatePCIProxy, setExpirationDatePCIProxy] = useState<string | null>(null);

    const ownerPhone = Form.useWatch("ownerPhone", form);

    useEffect(() => {
        // ownerPhone se rellena con setFieldValue (no lanza onFieldsChange): se recalcula aquí para no validar con el valor anterior
        refreshFormValidity();
    }, [ownerPhone]);



    const [isCancelling, setIsCancelling] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    // Campos que impiden confirmar la reserva (obligatorios vacíos o con errores); se listan en el desplegable del botón
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    // Desplegable con lo que falta, que se abre al pulsar "Confirmar reserva" cuando aún no se puede confirmar
    const [showConfirmIssues, setShowConfirmIssues] = useState<boolean>(false);
    const [tooltipInfo, setTooltipInfo] = useState<{ message: string; x: number; y: number } | null>(null);

    const paymentCurrency = (props.originalCurrency || '').toUpperCase();
    const isPaymentMethodCurrencySupported = (paymentMethod: PaymentMethodData): boolean => {
        const supportedCurrencies = paymentMethod?.currenciesSupported;

        // If backend does not send supported currencies, do not apply currency restriction.
        if (!Array.isArray(supportedCurrencies) || supportedCurrencies.length === 0) {
            return true;
        }

        if (!paymentCurrency) {
            return true;
        }

        return supportedCurrencies.some((currency) =>
            String(currency || '').toUpperCase() === paymentCurrency
        );
    };

    const selectedPaymentMethod = props.paymentsMethods.find(pm => pm.id === selectedPaymentType);
    const isSelectedPaymentTypeCurrencySupported = selectedPaymentMethod
        ? isPaymentMethodCurrencySupported(selectedPaymentMethod)
        : true;

    useEffect(() => {
        if (!selectedPaymentType) return;

        const selectedMethod = props.paymentsMethods.find(pm => pm.id === selectedPaymentType);
        if (selectedMethod && !isPaymentMethodCurrencySupported(selectedMethod)) {
            setSelectedPaymentType('');
        }
    }, [selectedPaymentType, props.paymentsMethods, props.originalCurrency]);

    const canConfirmBooking =
        isFormValid &&
        !isConfirmBooking &&
        selectedPaymentType !== "" &&
        isSelectedPaymentTypeCurrencySupported &&
        (!isPCIProxySelected || !!tokenPCIProxy);



    useEffect(() => {
        props.onFormValidityChange(canConfirmBooking);
        if (canConfirmBooking) setShowConfirmIssues(false); // ya no falta nada: se cierra el desplegable
    }, [canConfirmBooking]);



    // Campos del formulario que impiden confirmar la reserva: obligatorios vacíos o con errores de validación
    const getInvalidFields = (): string[] => {
        const fieldsWithErrors = form.getFieldsError()
            .filter(f => f.errors.length > 0)
            .map(f => f.name.join('.'));

        // Campos SIEMPRE obligatorios
        const baseRequiredFields = [
            "ownerName",
            "firstSurname",
            "ownerEmail",
            "ownerPhone",
        ];

        // Campos obligatorios SOLO si TPV
        const addressRequiredFields = isTPVSelected
            ? ["street1", "street2", "city", "postalCode", "country"]
            : [];

        const requiredFields = [
            ...baseRequiredFields,
            ...addressRequiredFields,
        ];

        const values = form.getFieldsValue(requiredFields);

        const emptyRequiredFields = requiredFields.filter(
            field => !values[field]
        );

        // En el orden de la página, para que la lista y "Ver campos pendientes" empiecen por el primero
        const pageOrder = ["ownerName", "firstSurname", "ownerEmail", "ownerP", "ownerPhone", "street1", "street2", "city", "postalCode", "country"];
        const position = (field: string) => {
            if (field.startsWith("room.")) return pageOrder.indexOf("street1") - 0.5; // huéspedes: entre el titular y la dirección
            const index = pageOrder.indexOf(field);
            return index === -1 ? pageOrder.length : index;
        };

        return [...emptyRequiredFields, ...fieldsWithErrors]
            .filter((field, index, fields) => fields.indexOf(field) === index)
            .sort((a, b) => position(a) - position(b));
    };

    const refreshFormValidity = () => {
        // validateFields() avisa a onFieldsChange al empezar, con los errores ya borrados: se espera al resultado.
        // Si no, el formulario parece válido un instante (y se cierra el desplegable o el botón pasa a enviar)
        if (form.isFieldsValidating()) return;
        const fields = getInvalidFields();
        setInvalidFields(fields);
        setIsFormValid(fields.length === 0);
    };

    useEffect(() => {
        // Sin TPV la dirección no es obligatoria: se quitan los errores que pudieran quedar de cuando lo era
        if (!isTPVSelected) {
            form.setFields(["street1", "street2", "city", "postalCode", "country"].map(name => ({ name, errors: [] })));
        }
        refreshFormValidity();
    }, [isTPVSelected]);

    // Etiqueta de cada campo para el desplegable del botón (ownerP es el Form.Item del teléfono; ownerPhone, su valor normalizado)

    const confirmFieldLabelKeys: { [field: string]: string } = {
        ownerName: 'components.booking-data-form.owner-data.name',
        firstSurname: 'components.booking-data-form.owner-data.firstSurname',
        ownerEmail: 'components.booking-data-form.owner-data.email',
        ownerPhone: 'components.booking-data-form.owner-data.phone',
        ownerP: 'components.booking-data-form.owner-data.phone',
        street1: 'components.booking-data-form.address-info.street-1',
        street2: 'components.booking-data-form.address-info.street-2',
        city: 'components.booking-data-form.address-info.city',
        postalCode: 'components.booking-data-form.address-info.postal-code',
        country: 'components.booking-data-form.address-info.country',
    };

    // Motivos por los que no se puede confirmar la reserva: las mismas condiciones que canConfirmBooking
    const confirmBookingIssues: string[] = [
        ...invalidFields.map(field => confirmFieldLabelKeys[field]
            ? String(t(confirmFieldLabelKeys[field]))
            : String(t('components.booking-data-form.confirm-requirements.invalid-field'))),
        ...(selectedPaymentType === "" || !isSelectedPaymentTypeCurrencySupported
            ? [String(t('components.booking-data-form.confirm-requirements.payment-method'))]
            : []),
        ...(isPCIProxySelected && !tokenPCIProxy
            ? [String(t('components.booking-data-form.confirm-requirements.card-data'))]
            : []),
    ].filter((issue, index, issues) => issues.indexOf(issue) === index);

    // Sin poder confirmar (y sin una confirmación en curso) el botón se ve deshabilitado pero sigue recibiendo el clic,
    // que abre o cierra el desplegable con lo que falta: un <button disabled> no recibe clics
    const isConfirmBlocked = !canConfirmBooking && !isConfirmBooking;

    const onConfirmIssuesOpenChange = (open: boolean) => {
        if (open && isConfirmBlocked) {
            form.validateFields().catch(() => { }); // marca en rojo los campos pendientes
        }
        setShowConfirmIssues(open && isConfirmBlocked);
    };

    // Cierra el desplegable y lleva al primer campo pendiente
    const goToFirstInvalidField = () => {
        setShowConfirmIssues(false);
        form.validateFields().catch(() => { });
        const firstField = invalidFields[0];
        if (firstField) {
            form.scrollToField(firstField === 'ownerPhone' ? 'ownerP' : firstField, { behavior: 'smooth', block: 'center' });
        }
    };



    return (
        <div className="booking-data-form">


            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onFieldsChange={() => {
                    refreshFormValidity();
                }}
            >

                  <NMIComponent
                    open={showNMI && isNMISelected}
                    hotelName={props.availData?.hotelData?.name || ""}
                    amount={Number(((cartContext.data?.rooms ?? []).reduce((total, room) => {
                        const base = cardPaymentOption === "agent"
                            ? (room.price.net ?? room.price.pvp ?? 0)
                            : room.price.isPvp ? (room.price.pvp ?? 0) : (room.price.net ?? 0) + (room.price.commission ?? 0);
                        return total + props.doExchangeCurrency(room.price.currency, props.originalCurrency, base);
                    }, 0)).toFixed(2))}
                    currency={props.originalCurrency}
                    tokenizationKey={tokenizationKeyNMI}
                    onSuccess={async (token) => {
                      const success = await getPaymentForm(bookingId, token);
                      setShowNMI(false);
                      if (success) {
                        setIsWaitingPayment(true);
                      } else {
                        setPreAuthorizationFailed(true);
                        setIsWaitingPayment(true);
                      }
                    }}
                    onCancel={() => {
                      setShowNMI(false);
                      deletePendingBooking(bookingId);
                    }}
                />



                <div className="app-box-form">
                    <Typography.Title level={3} className='app-title'>{t('components.booking-data-form.owner-data.title')}</Typography.Title>
                    <Row gutter={16}>
                        <Col xs={24} lg={12}>
                            {/* id propio: con el id por defecto (= name, "ownerDocument") el input pisa form.ownerDocument del DOM
                                y rompe los desplegables de antd dentro del formulario (Popover, Select...: "reading 'getComputedStyle'") */}
                            <Form.Item
                                name="ownerDocument" htmlFor="booking-owner-document" label={t('components.booking-data-form.owner-data.document')}>
                                <Input id="booking-owner-document" type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear onPressEnter={onFinish} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                rules={[{ required: true, message: t("forms.validations.required-field")! }]}
                                name="ownerName"
                                label={t('components.booking-data-form.owner-data.name')}
                                className="app-input">
                                <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear onPressEnter={onFinish} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                rules={[{ required: true, message: t("forms.validations.required-field")! }]}
                                name="firstSurname" label={t('components.booking-data-form.owner-data.firstSurname')}>
                                <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear onPressEnter={onFinish} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                name="secondSurname" label={t('components.booking-data-form.owner-data.secondSurname')}>
                                <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear onPressEnter={onFinish} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Form.Item
                                name="ownerEmail"
                                label={t('components.booking-data-form.owner-data.email')}
                                rules={[
                                    { required: true, message: t("forms.validations.required-field")! },
                                    {
                                        type: "email",
                                        message: t("forms.validations.invalid-format")!
                                    }
                                ]}
                            >
                                <Input
                                    type="text"
                                    onInput={(e: any) => e.target.value = e.target.value.toUpperCase()}
                                    className="app-input"
                                    allowClear
                                    onPressEnter={onFinish}
                                />
                            </Form.Item>
                        </Col>


                        <Col xs={24} lg={12}>
                            <Form.Item
                                required
                                validateFirst // vacío: solo "obligatorio"; si no, "teléfono no válido" (sin los dos a la vez)
                                name="ownerP"
                                label={t('components.booking-data-form.owner-data.phone')}
                                rules={[
                                    { required: true, message: t("forms.validations.required-field")! }, {
                                        validator: validatePhone
                                    }
                                ]}
                            >
                                <PhoneInput
                                    inputProps={{ id: 'ownerP' }} // para que "Ver campos pendientes" (scrollToField) encuentre el teléfono
                                    country="es"
                                    enableSearch
                                    preferredCountries={["es", "fr", "pt", "it", "de", "gb"]}
                                    autoFormat={true}
                                    disableCountryCode={false}
                                    countryCodeEditable={false}
                                    inputStyle={{ width: "100%" }}
                                    containerClass="app-phone-input-container"
                                    inputClass="app-input app-phone-input"
                                    buttonClass="app-phone-flag-button"
                                    dropdownClass="app-phone-dropdown"
                                    onChange={(value, country: any) => {
                                        const dialCode = (country)?.dialCode || "";
                                        const rawNational = value.startsWith(dialCode)
                                            ? value.slice(dialCode.length)
                                            : value;
                                        const nationalNumber = rawNational.replace(/^0+/, ""); // strip leading zero (e.g. UK: 07xxx → 7xxx)

                                        form.setFieldValue("ownerPhone", dialCode + "|" + nationalNumber);
                                        form.setFieldValue("ownerPhoneCountry", country.countryCode); // es, fr, it...

                                    }}
                                />
                            </Form.Item>
                        </Col>



                        <Col xs={24}>
                            <Form.Item name="commercialMails">
                                <Checkbox className="accept-commercial-emails">{t('components.booking-data-form.owner-data.accept-commercial-emails')}</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                <div className="app-box-form">
                    <Typography.Title level={3} className='app-title'>{t('components.booking-data-form.booking-data.title')}</Typography.Title>
                    {
                        cartContext.data.rooms.map((room, index) => {
                            return <div key={index}>
                                <RoomForm room={room} form={form} />
                            </div>
                        })
                    }
                </div>
                <div className="app-box-form">
                    <Typography.Title level={3} className='app-title'>{t('components.booking-data-form.additional-info.title')}</Typography.Title>
                    <Row gutter={16}>
                        <Col xs={24} lg={24}>
                            <Form.Item
                                name="observations"
                                label={t('components.booking-data-form.additional-info.observations')}>
                                <Input.TextArea className="app-input" allowClear rows={6} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>


                <div className="app-box-form">
                    <Typography.Title level={3} className='app-title'>{t('components.booking-data-form.address-info.title')}</Typography.Title>
                    <Row gutter={16}>
                        <Col xs={24} lg={24}>
                            <Form.Item
                                name="street1"
                                label={t('components.booking-data-form.address-info.street-1')}
                                rules={[
                                    {
                                        required: isTPVSelected,
                                        message: t("forms.validations.required-field") || '',
                                    },
                                ]}
                            >
                                <Input className="app-input" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={24}>
                            <Form.Item name="street2"
                                label={t('components.booking-data-form.address-info.street-2')}
                                rules={[
                                    {
                                        required: isTPVSelected,
                                        message: t("forms.validations.required-field") || '',
                                    },
                                ]}
                            >
                                <Input className="app-input" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={24}>
                            <Form.Item name="street3" label={t('components.booking-data-form.address-info.street-3')}>
                                <Input className="app-input" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={18}>
                            <Form.Item name="city"
                                label={t('components.booking-data-form.address-info.city')}
                                rules={[
                                    {
                                        required: isTPVSelected,
                                        message: t("forms.validations.required-field") || '',
                                    },
                                ]}
                            >
                                <Input className="app-input" allowClear />
                            </Form.Item>
                        </Col>

                        <Col xs={24} lg={6}>
                            <Form.Item
                                name="postalCode"
                                label={t('components.booking-data-form.address-info.postal-code')}
                                rules={[
                                    {
                                        required: isTPVSelected,
                                        message: t("forms.validations.required-field") || '',
                                    },
                                ]}
                            >
                                <Input className="app-input" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} lg={24}>
                            <Form.Item
                                name="country"
                                label={t('components.booking-data-form.address-info.country')}
                                rules={[
                                    {
                                        required: isTPVSelected,
                                        message: t("forms.validations.required-field") || '',
                                    },
                                ]}
                            >
                                {/*
                                    <Select
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        options={(props.countries ?? []).map(c => ({
                                            value: c.key,
                                            label: c.value
                                        }))}
                                    />*/}
                                <select // he tenido que meterlo nativo porque sino me da error muy raro con antd
                                    className="app-input app-native-select"
                                    defaultValue="">
                                    <option value="" disabled hidden>
                                        { }
                                    </option>
                                    {props.countries.map(c => (
                                        <option key={c.key} value={c.key}>{c.value}</option>
                                    ))}
                                </select>


                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                <div className="app-box-form">
                    <Typography.Title level={3} className='app-title'>{t('components.booking-data-form.payment-methods.title')}</Typography.Title>
                    <Col xs={24}>
                        <Radio.Group>
                            <Space direction="vertical">
                                {props.paymentsMethods
                                    .filter(paymentMethod => {
                                        // ¿Hay algún room no reembolsable?
                                        const isAnyNoRefundable = cartContext.data.rooms.some(
                                            (r) => r.rateInfo?.isNoRefundable === true
                                        );
                                        // 1️ Si hay tarifa NO REEMBOLSABLE → SOLO métodos TPV
                                        if (isAnyNoRefundable) {
                                            return paymentMethod.refCode.toLowerCase().includes("tpv");
                                        }

                                        // 2️ Si NO es no-reembolsable, aplicar regla de mínimo
                                        if (minimumAmount > 0) {
                                            return (
                                                paymentMethod.refCode !== "pho" &&
                                                paymentMethod.refCode !== "tbk"
                                            );
                                        }

                                        // 3️ En cualquier otro caso → mostrar todos
                                        return true;
                                    })
                                    .map(paymentMethod => {
                                        const isCurrencySupported = isPaymentMethodCurrencySupported(paymentMethod);
                                        const disableByCurrency = !isCurrencySupported;
                                        const disabledMessage = String(t('components.booking-data-form.payment-methods.disabled-by-config'));

                                        return (
                                            <div
                                                key={paymentMethod.id}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 0, position: 'relative' }}
                                            >
                                                <Radio
                                                    value={paymentMethod.id}
                                                    defaultChecked={selectedPaymentType === paymentMethod.id}
                                                    onChange={onChangeSelectedPaymentType}
                                                    checked={selectedPaymentType === paymentMethod.id}
                                                    disabled={disableByCurrency}
                                                >
                                                    {paymentMethod.name.replace(/\s?\(.*?\)/g, '')}
                                                </Radio> 
                                                {disableByCurrency && (
                                                    <InfoCircleOutlined
                                                        style={{ color: '#faad14', cursor: 'help', fontSize: 14 }}
                                                        onMouseEnter={(e) => {
                                                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                            setTooltipInfo({
                                                                message: disabledMessage,
                                                                x: rect.left,
                                                                y: rect.bottom +2
                                                            });
                                                        }}
                                                        onMouseLeave={() => setTooltipInfo(null)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })
                                }

                            </Space>
                        </Radio.Group>

                    </Col>
                    <Col xs={24} >

                        {selectedTPVMethod && (
                            <>
                                <Row gutter={50} className="tpv-info-container">
                                    <Col xs={24} className='tpv-col' >
                                        <Typography.Paragraph className="tpv-info-text">
                                            {t('components.booking-data-form.payment-methods.select-option-card-payment')}
                                        </Typography.Paragraph>
                                    </Col>
                                    <Col xs={24}>
                                        <Radio.Group
                                            onChange={onChangeCardPaymentOption}
                                            value={cardPaymentOption}
                                        >
                                            <Space direction="vertical">
                                                <Radio value="guest">
                                                    {t('components.booking-data-form.payment-methods.select-option-client')}
                                                </Radio>
                                                <Radio value="agent">
                                                    {t('components.booking-data-form.payment-methods.select-option-agent')}
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Col>

                                    <Col xs={24} className='tpv-col'  >
                                        <Typography.Paragraph className="tpv-info-text">
                                            {t('components.booking-data-form.payment-methods.select-quantity-payment')}
                                        </Typography.Paragraph>
                                    </Col>
                                    <Col xs={24}>
                                        <Radio.Group
                                            onChange={onChangeCardPaymentQuantity}
                                            value={cardPaymentQuantity}
                                        >
                                            <Space direction="vertical">
                                                <Radio disabled={minimumAmount == 0} value="minimum">
                                                    {t('components.booking-data-form.payment-methods.select-quantity-minimum')}
                                                    <MoneyComponent amountOriginal={minimumAmount} currencyOriginal={currencyData.currencySelectedISO} />  {t('components.booking-data-form.payment-methods.select-quantity-no-minimum')}
                                                </Radio>
                                                <Radio value="total">
                                                    {t('components.booking-data-form.payment-methods.select-quantity-total')}
                                                    <MoneyComponent amountOriginal={calculateTotalQuantityToPay()} currencyOriginal={currencyData.currencySelectedISO} />
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                    </Col>
                                </Row>

                                {isPCIProxySelected && merchantIdPCIProxy && (
                                    <div>
                                        <Divider />
                                        <div style={{ marginTop: 24 }}>
                                            <CardTokenizeForm
                                                merchantId={merchantIdPCIProxy}
                                                onSuccess={(transactionId, dateExpiration) => { setTokenPCIProxy(transactionId); setExpirationDatePCIProxy(dateExpiration) }} />
                                        </div>
                                        <div style={{ marginTop: 24 }}>

                                        </div>
                                    </div>
                                )}

                                <Modal
                                    className="app-modal"
                                    title={t("loading-payment.payment")}
                                    open={isWaitingPayment}
                                    footer={null}
                                    closable={false} // ← mientras se ejecuta, la X se desactiva
                                >
                                    {isCancelling && (
                                        <LoadingDataComponent text={t("loading-payment.wait-a-moment") || ""} />
                                    )}
                                    {!isCancelling && (
                                        <>
                                            {preAuthorizationFailed ? (
                                                <>
                                                    <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                        {t('loading-payment.payment-unsuccessful')}
                                                    </Typography.Paragraph>
                                                    <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                        {t('loading-payment.payment-retry')}
                                                    </Typography.Paragraph>
                                                    <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                        <Space align="center">
                                                            <Button
                                                                type="default"
                                                                className="app-button"
                                                                onClick={retryPreAuthorization}
                                                                loading={loadingRetry}
                                                                disabled={loadingRetry || loadingCancelation}
                                                            >
                                                                {t('button.retry')}
                                                            </Button>
                                                        </Space>
                                                    </Typography.Paragraph>


                                                    <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                        <Button
                                                            type="default"
                                                            className='app-button'
                                                            loading={loadingCancelation}
                                                            disabled={loadingCancelation || loadingRetry}
                                                            onClick={cancelBooking}
                                                        >
                                                            {t('button.cancel')}
                                                        </Button>
                                                    </Typography.Paragraph>

                                                </>

                                            ) : (
                                                <>
                                                    {!isNMISelected && (
                                                        <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                            {t('loading-payment.pay')}
                                                        </Typography.Paragraph>
                                                    )}

                                                    <Typography.Paragraph style={{ textAlign: "center", marginTop: 16 }}>
                                                        {t('loading-payment.please-wait')}
                                                    </Typography.Paragraph>

                                                    <LoadingDataComponent text=" " />
                                                    <Typography.Text style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
                                                        {t('loading-payment.remain-time')} {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")} {t("loading-payment.minutes")}
                                                    </Typography.Text>
                                                    <Button
                                                        type="default"
                                                        disabled
                                                        loading
                                                        block
                                                    >
                                                        {t('loading-payment.waiting')}
                                                    </Button>
                                                </>
                                            )}
                                        </>
                                    )}

                                </Modal>
                            </>
                        )}

                    </Col>
                    <Col xs={24} className="app-actions centered">
                        <Popover
                            trigger="click"
                            placement="top"
                            open={showConfirmIssues && isConfirmBlocked}
                            onOpenChange={onConfirmIssuesOpenChange}
                            content={
                                // El texto va dentro (no como title) para que el ancho máximo valga para todo el desplegable
                                <div style={{ maxWidth: 'min(320px, calc(100vw - 48px))' }}>
                                    <Typography.Text strong>{t('components.booking-data-form.confirm-requirements.title')}</Typography.Text>
                                    <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                                        {confirmBookingIssues.map((issue) => <li key={issue}>{issue}</li>)}
                                    </ul>
                                    {invalidFields.length > 0 && (
                                        <Button type="link" style={{ padding: 0 }} onClick={goToFirstInvalidField}>
                                            {t('components.booking-data-form.confirm-requirements.go-to-fields')}
                                        </Button>
                                    )}
                                </div>
                            }
                        >
                            <Button
                                htmlType={isConfirmBlocked ? 'button' : 'submit'}
                                ref={payButtonRef} // Asignamos la referencia al botón
                                disabled={isConfirmBooking}
                                aria-disabled={isConfirmBlocked || undefined}
                                // Mismo aspecto que deshabilitado (antd pinta igual :disabled y .ant-btn-disabled); el radio es el de button:disabled del tema ($app-border-radius)
                                className={`app-button${isConfirmBlocked ? ' ant-btn-disabled' : ''}`}
                                style={isConfirmBlocked ? { borderRadius: 2 } : undefined}
                                size='large'
                                onClick={(e) => {
                                    // Bloqueado nunca envía: si este mismo clic revalida y deja el formulario bien,
                                    // React puede cambiar el tipo a "submit" antes de que el navegador termine el clic
                                    if (isConfirmBlocked) e.preventDefault();
                                }}
                            >
                                {
                                    isConfirmBooking ? <Spin /> : <span className={`sol-icon-ok-circled`} style={{ color: 'white' }} />
                                }
                                {t('components.booking-data-form.actions.confirm-booking')}


                            </Button>
                        </Popover>
                    </Col>
                </div>
            </Form>
        {tooltipInfo && createPortal(
            <div
                className="payment-method-tooltip"
                style={{ top: tooltipInfo.y, left: tooltipInfo.x }}
            >
                {tooltipInfo.message}
            </div>,
            document.body
        )}
    </div>
    );
});

interface RoomFormProps {
    room: CartRoomData,
    form: any,
}
const RoomForm = (props: RoomFormProps) => {

    const { room, form } = props;

    const { t } = useTranslation();

    const numAdults = room.occupancy.adults;
    const numChilds = room.occupancy.childs;
    const childAges = room.occupancy.childsAges;
    const { doExchangeCurrency, currencyData } = useCurrencyData();

    const copyOfOwner = () => {
        form.setFieldsValue({
            [`room.${room.roomIndex - 1}.adult.${0}.name`]: form.getFieldValue("ownerName"),
            [`room.${room.roomIndex - 1}.adult.${0}.firstSurname`]: form.getFieldValue("firstSurname"),
            [`room.${room.roomIndex - 1}.adult.${0}.secondSurname`]: form.getFieldValue("secondSurname"),
        });
    }

    const getRowAdult = (index: number) => {
        return <Row gutter={16}>
            <Col xs={24} className={index === 0 ? 'copy-of-titular' : ''}>
                <Typography.Title level={4} className='guest-type-title'>{t('components.booking-data-form.booking-data.adult-number', [index + 1])}</Typography.Title>
                {
                    index === 0 ? <Button className="app-button" size='small' onClick={copyOfOwner}>{t('components.booking-data-form.booking-data.copy-of-owner')}</Button> : <></>
                }
            </Col>
            <Col xs={24} lg={8}>
                <Form.Item name={`room.${room.roomIndex - 1}.adult.${index}.name`} label={t('components.booking-data-form.booking-data.name')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
                <Form.Item name={`room.${room.roomIndex - 1}.adult.${index}.firstSurname`} label={t('components.booking-data-form.booking-data.firstSurname')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
                <Form.Item name={`room.${room.roomIndex - 1}.adult.${index}.secondSurname`} label={t('components.booking-data-form.booking-data.secondSurname')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
        </Row>
    }

    const getRowChild = (index: number) => {
        const age = childAges[index];

        return <Row gutter={16}>
            <Col xs={24}>
                <Typography.Title level={4} className='guest-type-title'>{t('components.booking-data-form.booking-data.child-number', [index + 1])}</Typography.Title>
            </Col>
            <Col xs={24} lg={6}>
                <Form.Item name={`room.${room.roomIndex - 1}.childs.${index}.name`} label={t('components.booking-data-form.booking-data.name')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
                <Form.Item name={`room.${room.roomIndex - 1}.childs.${index}.firstSurname`} label={t('components.booking-data-form.booking-data.firstSurname')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
                <Form.Item name={`room.${room.roomIndex - 1}.childs.${index}.secondSurname`} label={t('components.booking-data-form.booking-data.secondSurname')}>
                    <Input type="text" onInput={(e: any) => e.target.value = e.target.value.toUpperCase()} className="app-input" allowClear />
                </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
                <Form.Item
                    rules={[{ required: true, message: t("forms.validations.required-field")! }]}
                    initialValue={age} name={`room.${room.roomIndex - 1}.childs.${index}.age`} label={t('components.booking-data-form.booking-data.age')}>
                    <Input disabled value={childAges[index]} type="number" className="app-input" allowClear />
                </Form.Item>
            </Col>
        </Row>
    }

    const [formData, setFormData] = useState<Array<JSX.Element>>([])

    useEffect(() => {

        let data = [];
        for (let i = 0; i < numAdults; i++) {
            data.push(getRowAdult(i));
        }

        for (let i = 0; i < numChilds; i++) {
            data.push(getRowChild(i));
        }
        setFormData(data);
    }, []);

    return (
        <>
            <Col xs={24}>
                <Typography.Title level={4} className='room-title'>{t('components.booking-data-form.booking-data.header-occupancy', [(room.roomIndex), room.name, numAdults, numChilds])}. {doExchangeCurrency(room.price.currency, currencyData.currencySelectedISO, ((room.price.isPvp ? (room.price.pvp) : (room.price.net + room.price.commission))))} {currencyData.currencySelectedISO}</Typography.Title>
            </Col>
            {formData}
        </>
    )
}

export default BookingDataForm;